import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Screen } from '@/components/Screen'
import { Button } from '@/components/Button'
import { Field, Select, TextArea, TextInput } from '@/components/Field'
import { Kicker } from '@/components/Kicker'
import { useAppStore } from '@/stores/appStore'
import { useFocusedPerson } from '@/hooks/useFocusedPerson'
import { PACK_COLORS } from '@/design-system/tokens'
import { absoluteTime, relativeTime, toLocalInputValue } from '@/lib/format'
import { emitEvent } from '@/lib/emitEvent'
import { uploadAttachment } from '@/lib/attachments'
import { PhotoAttachInput } from '@/components/PhotoAttachInput'
import { supabase } from '@/lib/supabase'
import type { RecentMedicationCheck } from '@/lib/types'
import { correctionEventType, payloadValue, useEditableEvent } from '@/lib/editEvent'

/**
 * Medication logger — HIGH-STAKES pack per DEC-019.
 * Requires: explicit medication name, dose amount + unit, explicit confirm checkbox.
 * Two-caregiver duplicate dose guard: check_recent_medication() called before save.
 * Never auto-fills medication name or dose — parent types it every time.
 */
export function MedicationLoggerScreen() {
  const nav = useNavigate()
  const user = useAppStore((s) => s.user)
  const familyId = useAppStore((s) => s.familyId)
  const person = useFocusedPerson()
  const edit = useEditableEvent(['early_care.medication.recorded', 'early_care.medication.corrected'])

  const [name, setName] = useState('')
  const [amount, setAmount] = useState('')
  const [amountUnit, setAmountUnit] = useState<'mL' | 'mg' | 'drops' | 'tsp' | 'tablet' | 'other'>('mL')
  const [route, setRoute] = useState<'oral' | 'topical' | 'rectal' | 'other'>('oral')
  const [occurredAt, setOccurredAt] = useState<string>(toLocalInputValue(new Date()))
  const [note, setNote] = useState('')
  const [confirmed, setConfirmed] = useState(false)
  const [photos, setPhotos] = useState<File[]>([])
  const [check, setCheck] = useState<RecentMedicationCheck | null>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [prefilled, setPrefilled] = useState(false)

  useEffect(() => {
    if (!edit.original || prefilled) return
    const p = edit.original.payload
    setName(payloadValue<string>(p, 'medication_name') ?? '')
    const amt = payloadValue<number>(p, 'amount')
    if (amt != null) setAmount(String(amt))
    const unit = payloadValue<typeof amountUnit>(p, 'amount_unit')
    if (unit) setAmountUnit(unit)
    const rt = payloadValue<typeof route>(p, 'route')
    if (rt) setRoute(rt)
    const n = payloadValue<string>(p, 'note')
    if (n) setNote(n)
    setOccurredAt(toLocalInputValue(new Date(edit.original.occurred_at)))
    setPrefilled(true)
  }, [edit.original, prefilled])

  useEffect(() => {
    if (!person || !name.trim()) { setCheck(null); return }
    const t = setTimeout(async () => {
      const { data, error } = await supabase.rpc('check_recent_medication', {
        p_person_id: person.id,
        p_medication_name: name.trim(),
        p_lookback_hours: 6,
      })
      if (error) { setCheck(null); return }
      const row = (Array.isArray(data) ? data[0] : data) as RecentMedicationCheck | null
      setCheck(row ?? null)
    }, 300)
    return () => clearTimeout(t)
  }, [name, person])

  if (!person || !familyId || !user) return <Screen title="Medication" back />

  const packColor = PACK_COLORS.health.light

  async function save(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true); setErr(null)
    try {
      if (!person || !familyId || !user) return
      if (!name.trim()) throw new Error('Medication name is required')
      const amt = parseFloat(amount)
      if (Number.isNaN(amt) || amt <= 0) throw new Error('Enter a dose amount')
      if (!confirmed) throw new Error('Please confirm the dose before saving')

      const payload: Record<string, unknown> = {
        medication_name: name.trim(),
        amount: amt,
        amount_unit: amountUnit,
        route,
        confirmed_by_user_id: user.id,
        ...(note.trim() ? { note: note.trim() } : {}),
      }

      const eventType = edit.original
        ? (correctionEventType(edit.original.event_type) as 'early_care.medication.corrected')
        : 'early_care.medication.recorded'

      if (photos.length > 0) payload.has_attachment = true

      const eventId = await emitEvent({
        familyId,
        personId: person.id,
        userId: user.id,
        eventType,
        payload: payload as import('@/lib/types').JSONValue,
        occurredAt: new Date(occurredAt),
        supersedes: edit.supersedesId,
      })
      for (const file of photos) {
        await uploadAttachment({ familyId, userId: user.id, file, eventId })
      }
      if (edit.original) nav('/timeline'); else nav(-1)
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const hasRecentDose = check && check.recent_dose_count > 0

  const isEditing = !!edit.original
  return (
    <Screen title={isEditing ? 'Correct medication' : 'Log medication'} back>
      <form onSubmit={save} className="px-5 py-5 space-y-6">
        {edit.loading && <div className="text-caption text-text-tertiary">Loading original entry…</div>}
        {edit.error && (
          <div className="rounded-r-sm bg-pack-safety/15 border border-pack-safety/30 p-3 text-caption text-pack-safety">{edit.error}</div>
        )}
        {isEditing && (
          <div className="rounded-r-sm bg-bg-elevated border border-divider-subtle p-3 text-caption text-text-secondary">
            Correcting an earlier medication entry. Saving creates a new dose entry that supersedes the original — the original stays in the record. Confirm the values as you normally would.
          </div>
        )}
        <div
          className="rounded-r-sm border p-3"
          style={{ background: PACK_COLORS.safety.light + '18', borderColor: PACK_COLORS.safety.light + '55' }}
        >
          <Kicker color={PACK_COLORS.safety.light}>Confirm every dose</Kicker>
          <p className="mt-1 text-caption text-text-secondary">
            No auto-fill. No presets. Type the medication name and dose every time — this pack is high-stakes.
          </p>
        </div>

        <Field label="Medication name">
          <TextInput
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Tylenol infant"
            required
            autoFocus
          />
        </Field>

        {hasRecentDose && (
          <div
            className="rounded-r-sm border p-3"
            style={{ background: PACK_COLORS.safety.light + '18', borderColor: PACK_COLORS.safety.light + '55' }}
          >
            <Kicker color={PACK_COLORS.safety.light}>Duplicate dose warning</Kicker>
            <p className="mt-1 text-caption text-text-secondary">
              A dose of <strong>{name.trim()}</strong> was already logged {relativeTime(check!.most_recent_at)} (
              {absoluteTime(check!.most_recent_at)}
              {check!.most_recent_amount != null && `, ${check!.most_recent_amount} ${check!.most_recent_amount_unit ?? ''}`}
              ). Please double-check with the other caregiver before saving another dose.
            </p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Field label="Amount">
            <TextInput
              type="number"
              inputMode="decimal"
              step="0.01"
              min={0}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="e.g. 2.5"
              required
            />
          </Field>
          <Field label="Unit">
            <Select value={amountUnit} onChange={(e) => setAmountUnit(e.target.value as typeof amountUnit)}>
              <option value="mL">mL</option>
              <option value="mg">mg</option>
              <option value="drops">drops</option>
              <option value="tsp">tsp</option>
              <option value="tablet">tablet(s)</option>
              <option value="other">other</option>
            </Select>
          </Field>
        </div>

        <Field label="Route">
          <Select value={route} onChange={(e) => setRoute(e.target.value as typeof route)}>
            <option value="oral">Oral</option>
            <option value="topical">Topical</option>
            <option value="rectal">Rectal</option>
            <option value="other">Other</option>
          </Select>
        </Field>

        <Field label="When">
          <TextInput type="datetime-local" value={occurredAt} onChange={(e) => setOccurredAt(e.target.value)} />
        </Field>

        <Field label="Note (optional)">
          <TextArea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Symptoms, why, etc." />
        </Field>

        <PhotoAttachInput
          photos={photos}
          onChange={setPhotos}
          label="Photos (optional)"
          hint="Prescription bottle, dosing cup — proof-of-dose for the record."
          compact
        />

        <label className="flex items-start gap-3 rounded-r-sm border border-divider-subtle p-3 bg-bg-elevated cursor-pointer">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            className="mt-1 h-5 w-5 accent-pack-safety"
          />
          <div className="text-caption text-text-secondary">
            I confirm the medication name, dose amount and unit are correct, and this dose has not already been given by another caregiver.
          </div>
        </label>

        {err && (
          <div className="rounded-r-sm bg-pack-safety/15 border border-pack-safety/30 p-3 text-caption text-pack-safety">
            {err}
          </div>
        )}

        <Button
          type="submit"
          variant="primary"
          size="lg"
          packColor={packColor}
          loading={busy}
          disabled={!confirmed}
          className="w-full"
        >
          {isEditing ? 'Save correction' : 'Save dose'}
        </Button>
      </form>
    </Screen>
  )
}
